"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { RangeInput } from "@/components/ui/range-input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, ChevronRight, ChevronLeft, Calendar as CalendarIcon, MapPin, DollarSign, CircleCheck } from 'lucide-react';
const inputBaseClass = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50";
const textareaBaseClass = "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50";
export default function TellUsAboutYourDreamTrip() {
  return <section data-section-id="224122" id="start-your-project" className="py-24 dark bg-secondary text-secondary-foreground">
            <div className="container mx-auto px-4 max-w-2xl">
                <div className="text-center mb-10">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground font-default mb-4">
                        Ready to clear the way?
                    </h2>
                    <p className="text-muted-foreground max-w-xl mx-auto">
                        Get in touch with CHIPPER MULCHING CO. to begin transforming your property with our low-impact solutions.
                    </p>
                </div>

                <FormProvider formKey="start_project_form_home" formId="start_project_form_home" sectionName="tell-us-about-your-dream-trip" defaultValues={{
        name: "",
        email: "",
        phone: "",
        destination: "",
        travel_start_date: "",
        travel_end_date: "",
        budget: 2000,
        interests: [],
        additional_details: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        destination: {
          required: true
        },
        travel_start_date: {
          required: true
        },
        travel_end_date: {
          required: true
        }
      }} submitText="Get My Free Quote" successMessage="Thank you! We've received your project details and will be in touch shortly to provide your free estimate.">
                    <MultiStepForm />
                </FormProvider>
            </div>
        </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps));
  const prevStep = () => setStep(s => Math.max(s - 1, 1));
  const formatLocalDate = (d: Date) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  if (isSubmitted && !submitError) {
    return <Card className="shadow-xl border-border/20 bg-card/30 backdrop-blur-md text-center py-12 rounded-2xl">
                <CardContent className="flex flex-col items-center gap-4">
                    <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center text-primary mb-4">
                        <CircleCheck className="size-8" />
                    </div>
                    <CardTitle className="text-2xl font-default text-foreground">Quote Request Received!</CardTitle>
                    <p className="text-muted-foreground">{successMessage}</p>
                    <Button onClick={() => {
          resetForm();
          setStep(1);
        }} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-lg">
                        Request Another Quote
                    </Button>
                </CardContent>
            </Card>;
  }
  return <Card className="shadow-2xl border-border/20 bg-card/20 backdrop-blur-lg overflow-hidden rounded-2xl">
            <div className="bg-muted/10 px-6 py-4 border-b border-border/20">
                <div className="flex justify-between text-sm font-medium text-muted-foreground mb-2">
                    <span>Step {step} of {totalSteps}</span>
                    <span>{Math.round(step / totalSteps * 100)}% Completed</span>
                </div>
                <Progress value={step / totalSteps * 100} className="h-2 bg-muted/20" />
            </div>

            <Form onSubmit={handleSubmit}>
                <CardContent className="p-6 sm:p-8">
                    {}
                    <div className={step === 1 ? "space-y-8 block animate-in fade-in slide-in-from-right-4" : "hidden"}>
                        <div className="space-y-2">
                            <h3 className="text-xl font-semibold font-default flex items-center gap-2 text-foreground">
                                <MapPin className="size-5 text-primary" /> Project Location
                            </h3>
                            <p className="text-sm text-muted-foreground">Select the primary service area for your project.</p>
                        </div>

                        <FormField control={control} name="destination" rules={{
            required: "Please select a service area"
          }} render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-foreground">Service Location *</FormLabel>
                                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                                        <FormControl>
                                            <SelectTrigger className="w-full bg-background border-border/30 rounded-lg">
                                                <SelectValue placeholder="e.g., Burlington, Greensboro, High Point" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="Burlington">Burlington</SelectItem>
                                            <SelectItem value="Greensboro">Greensboro</SelectItem>
                                            <SelectItem value="High Point">High Point</SelectItem>
                                            <SelectItem value="Mebane">Mebane</SelectItem>
                                            <SelectItem value="Graham">Graham</SelectItem>
                                            <SelectItem value="Other">Other Piedmont Triad Area</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>} />

                        <div className="space-y-4 pt-4 border-t border-border/20">
                            <div className="space-y-2">
                                <h3 className="text-xl font-semibold font-default flex items-center gap-2 text-foreground">
                                    <SafeHydrate><CalendarIcon className="size-5 text-primary" /></SafeHydrate> Preferred Timeline
                                </h3>
                                <p className="text-sm text-muted-foreground">When would you like us to start clearing?</p>
                            </div>

                            <FormField control={control} name="travel_start_date" rules={{
              required: "Start date is required"
            }} render={({
              field: startField
            }) => <FormField control={control} name="travel_end_date" rules={{
              required: "End date is required"
            }} render={({
              field: endField
            }) => {
              const fromDate = startField.value ? new Date(startField.value) : undefined;
              const adjustedFrom = fromDate ? new Date(fromDate.getTime() + fromDate.getTimezoneOffset() * 60000) : undefined;
              const toDate = endField.value ? new Date(endField.value) : undefined;
              const adjustedTo = toDate ? new Date(toDate.getTime() + toDate.getTimezoneOffset() * 60000) : undefined;
              return <FormItem className="flex flex-col items-center bg-background/40 p-4 rounded-xl border border-border/20">
                                                    <FormControl>
                                                        <SafeHydrate><Calendar mode="range" selected={{
                      from: adjustedFrom,
                      to: adjustedTo
                    }} onSelect={range => {
                      if (range?.from) {
                        startField.onChange(formatLocalDate(range.from));
                      } else {
                        startField.onChange('');
                      }
                      if (range?.to) {
                        endField.onChange(formatLocalDate(range.to));
                      } else {
                        endField.onChange('');
                      }
                    }} numberOfMonths={1} className="bg-transparent text-foreground" /></SafeHydrate>
                                                    </FormControl>
                                                    <div className="flex w-full justify-between mt-4 text-sm px-4">
                                                        <div className="flex flex-col">
                                                            <span className="text-muted-foreground">Earliest:</span>
                                                            <span className="font-medium text-foreground">{startField.value || "Not selected"}</span>
                                                        </div>
                                                        <div className="flex flex-col text-right">
                                                            <span className="text-muted-foreground">Latest:</span>
                                                            <span className="font-medium text-foreground">{endField.value || "Not selected"}</span>
                                                        </div>
                                                    </div>
                                                    <FormMessage />
                                                    {endField.error && <p className="text-sm font-medium text-destructive mt-2">{endField.error.message}</p>}
                                                </FormItem>;
            }} />} />
                        </div>
                    </div>

                    {}
                    <div className={step === 2 ? "space-y-8 block animate-in fade-in slide-in-from-right-4" : "hidden"}>
                        <div className="space-y-2">
                            <h3 className="text-xl font-semibold font-default flex items-center gap-2 text-foreground">
                                <DollarSign className="size-5 text-primary" /> Project Scope & Services
                            </h3>
                            <p className="text-sm text-muted-foreground">Help us understand the scale of your land clearing needs.</p>
                        </div>

                        <FormField control={control} name="budget" render={({
            field
          }) => <FormItem className="bg-background/40 p-6 rounded-xl border border-border/20">
                                    <div className="flex justify-between items-center mb-4">
                                        <FormLabel className="text-base text-foreground">Estimated Acreage / Size</FormLabel>
                                        <span className="text-lg font-semibold text-primary">{field.value || 0} sq ft</span>
                                    </div>
                                    <FormControl>
                                        <RangeInput min={500} max={15000} step={500} value={field.value || 2000} onValueChange={field.onChange} className="mt-2" />
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground mt-2">Approximate area to be cleared (optional)</p>
                                    <FormMessage />
                                </FormItem>} />

                        <FormField control={control} name="interests" render={({
            field
          }) => {
            const currentValues = Array.isArray(field.value) ? field.value : [];
            const interestOptions = ["Forestry Mulching", "Land Clearing", "Path & Trail Clearing", "Driveway Clearing", "Brush Removal", "Lot Preparation", "Overgrowth Removal"];
            return <FormItem>
                                        <FormLabel className="text-base text-foreground">Which Services Do You Need?</FormLabel>
                                        <p className="text-sm text-muted-foreground mb-4">Select all that apply</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                                            {interestOptions.map((option, i) => <label key={i} data-index={i} className="flex items-center space-x-3 cursor-pointer p-3 rounded-lg border border-border/30 hover:bg-accent/20 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/10">
                                                    <input type="checkbox" className="rounded border-input text-primary focus:ring-ring size-4 accent-primary" checked={currentValues.includes(option)} onChange={e => {
                    const newValues = e.target.checked ? [...currentValues, option] : currentValues.filter(v => v !== option);
                    field.onChange(newValues);
                  }} />
                                                    <span className="text-sm font-medium text-foreground">{option}</span>
                                                </label>)}
                                        </div>
                                        <FormMessage />
                                    </FormItem>;
          }} />
                    </div>

                    {}
                    <div className={step === 3 ? "space-y-6 block animate-in fade-in slide-in-from-right-4" : "hidden"}>
                        <div className="space-y-2 mb-6">
                            <h3 className="text-xl font-semibold font-default text-foreground">Almost Done!</h3>
                            <p className="text-sm text-muted-foreground">Provide your contact details so we can send you a professional quote.</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <FormField control={control} name="name" rules={{
              required: "Name is required"
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground">Your Name *</FormLabel>
                                        <FormControl>
                                            <input type="text" placeholder="John Smith" className={inputBaseClass} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>} />

                            <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground">Email Address *</FormLabel>
                                        <FormControl>
                                            <input type="email" placeholder="john@example.com" className={inputBaseClass} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>} />
                        </div>

                        <FormField control={control} name="phone" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-foreground">Phone Number</FormLabel>
                                    <FormControl>
                                        <input type="tel" placeholder="(336) 555-0123" className={inputBaseClass} {...field} />
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground mt-1">We'll call you to discuss project specifics</p>
                                    <FormMessage />
                                </FormItem>} />

                        <FormField control={control} name="additional_details" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="text-foreground">Project Details or Special Requests</FormLabel>
                                    <FormControl>
                                        <textarea placeholder="Tell us about the terrain, specific trees to keep, or any accessibility concerns..." className={textareaBaseClass} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>} />

                        {submitError && <div className="p-4 bg-destructive/20 border border-destructive/30 rounded-lg mt-4">
                                <p className="text-sm text-destructive font-medium flex items-center gap-2">
                                     {submitError}
                                </p>
                            </div>}
                    </div>
                </CardContent>

                <CardFooter className="flex justify-between p-6 sm:px-8 border-t border-border/20 bg-muted/5">
                    <Button type="button" variant="outline" onClick={prevStep} disabled={step === 1 || isSubmitting} className={step === 1 ? "invisible" : "border-border/40 text-foreground hover:bg-accent/20 rounded-lg"}>
                        <ChevronLeft className="mr-2 size-4" /> Back
                    </Button>

                    {step < totalSteps ? <Button type="button" onClick={nextStep} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-semibold">
                            Next Step <ChevronRight className="ml-2 size-4" />
                        </Button> : <Button type="submit" disabled={isSubmitting} className="min-w-[140px] bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-bold shadow-lg">
                            {isSubmitting ? <>
                                    <Loader className="animate-spin mr-2 size-4" />
                                    Sending...
                                </> : 'Get My Free Quote'}
                        </Button>}
                </CardFooter>
            </Form>
        </Card>;
}